import { useGameState } from '@/stores/gameState'
import type { RoomState } from '@acepoker/contract/acepoker'
import { useAuth } from '@/stores/auth'
import { getLoaderScriptUrl } from './env'
import { mountCommentatorUIWebComponent } from './commentator'
import { mountChatUIWebComponent } from './chat'
import type { FlagsReturnType } from '@/composables/useFeatureFlags'
export const assistantState: {
  tableId?: string
  gameState?: RoomState
  numberOfPlayers?: number
  currentPlayerId?: string
  gameCode?: string
  strategyApiPayload?: any
  scriptLoadPromise?: Promise<void>
} = {}

const commentatorScriptId = 'commentator'

export function checkInjectAssistantJs() {
  if (!document.getElementById(commentatorScriptId)) {
    let scriptLoadResolve: () => void
    assistantState.scriptLoadPromise = new Promise<void>((resolve) => (scriptLoadResolve = resolve))

    // Defer script loading until after UI is done loading
    const loadScript = () => {
      const script = document.createElement('script')
      const loaderUrl = getLoaderScriptUrl()

      script.id = commentatorScriptId
      script.src = `${loaderUrl}/loader.js`
      script.async = true // Make script non-blocking

      script.onload = () => scriptLoadResolve()
      script.onerror = (
        event: Event | string,
        source?: string,
        lineno?: number,
        colno?: number,
        error?: Error
      ) => {
        console.error(event, (event as any)?.message, (event as any)?.error, error)
        scriptLoadResolve()
      }

      document.body.appendChild(script)
    }

    // Use requestIdleCallback for better performance, fallback to setTimeout
    if (window.requestIdleCallback) {
      window.requestIdleCallback(loadScript)
    } else {
      setTimeout(loadScript, 0)
    }
  }

  return assistantState.scriptLoadPromise
}

export type InitAssistantProps = {
  tableId: string | undefined
  playerId: string | undefined
  gameCode: string
  mode: 'home' | 'compete' | 'drill' | 'player-chat-only'
  enableCommentatorMode: boolean
  flags: FlagsReturnType
}

export function initAssistant({ tableId, playerId, gameCode, mode, flags }: InitAssistantProps) {
  const state = assistantState
  state.tableId = tableId
  state.gameCode = gameCode
  state.currentPlayerId = playerId

  const enableCommentatorMode = flags.isEnabled('quintace_commentator_mode').value

  useGameState().onGameStateUpdate(({ state: gameState, playerId }) => {
    if (enableCommentatorMode) {
      const currentHand = state.strategyApiPayload?.hand
      const newHand: any = (gameState.strategyApiPayload as any)?.hand
      const currentCommunityCards = state.gameState?.hand?.communityCards
      const newCommunityCards = gameState?.hand?.communityCards
      const newWinners = gameState?.hand?.winners
      const oldWinners = state.gameState?.hand?.winners
      const isShowingWinners = !!newWinners?.length

      const currentActions = currentHand?.actions?.entries
      const newActions = newHand?.actions?.entries
      const lastAction = newActions?.[newActions.length - 1]

      const players = gameState?.players?.map((p) => {
        const seatNo = gameState.seats.findIndex((s) => s?.playerId === p.playerId)
        const seat = gameState.seats[seatNo]
        let winAmount: number | null = isShowingWinners ? (seat?.stack || 0) - p.stack : null

        return {
          seatNo,
          winAmount,
          ...p
        }
      })

      if (newHand?.players && gameState?.players) {
        newHand.players = newHand.players.map((p: any) => {
          const player = players.find((pl) => pl.seatNo === p.seat_no)
          const holeCards = player?.playerId ? gameState.hand?.holeCards?.[player?.playerId] : null
          return {
            ...p,
            hole_cards: holeCards?.filter(Boolean)?.join('') || null,
            win_amount: player?.winAmount
          }
        })
      }
      const myPlayer = players.find((p) => p.playerId === playerId)

      const showConditions = {
        basic: Boolean(mode === 'compete' && newActions?.length),
        communityCardsChanged: Boolean(
          newCommunityCards?.length && currentCommunityCards?.length !== newCommunityCards?.length
        ),
        currentPlayerMadeAction: Boolean(
          myPlayer &&
            currentActions?.length !== newActions?.length &&
            lastAction?.seat_no === myPlayer?.seatNo
        ),
        showWinners: Boolean(newWinners?.length && newWinners.length !== oldWinners?.length)
      }

      const loadCommentary =
        showConditions.basic &&
        (showConditions.communityCardsChanged ||
          showConditions.currentPlayerMadeAction ||
          showConditions.showWinners)

      const displayCommentary =
        showConditions.basic && (showConditions.communityCardsChanged || showConditions.showWinners)

      if (loadCommentary) {
        mountCommentatorUIWebComponent(newHand, playerId, displayCommentary)
      }
    }

    state.gameState = JSON.parse(JSON.stringify(gameState))
    state.currentPlayerId = playerId

    if (
      state.numberOfPlayers !== gameState.players?.length &&
      !gameState.gameConfig?.assessmentMode
    ) {
      if (state.tableId) {
        const player = gameState.players?.find((p) => p.playerId === playerId)

        mountChatUIWebComponent({
          userId: useAuth().player?.id?.toString() || '1',
          playerId,
          tableId: state.tableId,
          mode,
          nickname: player?.name,
          flags
        })
      } else {
        console.error('mountChatUIWebComponent: Table ID not found')
      }
      state.numberOfPlayers = gameState.players?.length || 0
    }

    state.strategyApiPayload = gameState.strategyApiPayload
  }, 'assistant_mode')
}

export function cleanupAssistant() {
  const gameState = useGameState()
  gameState.offGameStateUpdate('assistant_mode')

  // Reset assistant state
  Object.keys(assistantState).forEach((key) => {
    delete assistantState[key as keyof typeof assistantState]
  })
}
